/**
 * Centralized registry for ALL notification-related React Query keys.
 *
 * No raw strings should be used for notification cache keys anywhere in the
 * codebase — every producer (`useNotifications`, `useNotificationCount`) and
 * every invalidator (`useRealtimeInvalidation`, `useBlockUser`,
 * `useClaimDeclineQuestion`) goes through these factories so the key shape
 * can't drift between them.
 *
 * Byte-compatible successors to the legacy `notificationQueryKeys` object
 * (useNotifications.ts) and `NOTIFICATION_COUNT_KEY` const
 * (useNotificationCount.ts). See CLAUDE.md "Cache keys: NEVER use raw strings".
 */

/**
 * Default page size for the notifications list. Single source of truth shared by
 * BOTH the `useNotifications` hook (its `per_page` default) and the
 * `notificationsListKey` factory default below, so the two can't drift. If they
 * did, `Notifications.tsx`'s bare `notificationsListKey()` optimistic write would
 * target a dead cache entry (the hook reading a different key) and the
 * claim-status badge would silently stop updating until a full refetch.
 */
export const DEFAULT_NOTIFICATIONS_PER_PAGE = 25;

/**
 * Base / prefix key for the notifications list family. Invalidating this sweeps
 * every paginated `notificationsListKey(per_page)` entry in one call.
 */
export const notificationsBaseKey = () => ['notifications'] as const;

/**
 * Paginated notifications list. `per_page` is part of the key so different page
 * sizes stay distinct cache entries. The default is the shared
 * `DEFAULT_NOTIFICATIONS_PER_PAGE` (also `useNotifications`' default) so a bare
 * `notificationsListKey()` optimistic write hits the same entry the hook produced.
 */
export const notificationsListKey = (per_page = DEFAULT_NOTIFICATIONS_PER_PAGE) =>
  ['notifications', { per_page }] as const;

/**
 * Unread notification count (bell badge). A separate root from the list so the
 * count can be polled / invalidated independently of the list pages.
 */
export const notificationCountKey = () => ['notification-count'] as const;
